import {
  ImageBlockEditing,
  ImageEditing,
  ImagePlaceholder,
  ImageSizeAttributes,
  ImageTypeCommand,
  downcastImageAttribute,
  downcastSrcsetAttribute
} from "./chunk-MBUPU22L.js";
import "./chunk-5UJG77VP.js";
import {
  ImageUtils,
  createInlineImageViewElement,
  determineImageTypeForInsertionAtSelection,
  getImgViewElementMatcher
} from "./chunk-MPGKEZT5.js";
import "./chunk-SA55FI2N.js";
import {
  ClipboardPipeline
} from "./chunk-UHWQXN2Z.js";
import {
  Widget
} from "./chunk-ZGNJRE43.js";
import "./chunk-6GJUXDIH.js";
import "./chunk-W7WGVHCV.js";
import "./chunk-NICE7ARI.js";
import "./chunk-U5S3T6FN.js";
import "./chunk-GJCFV3G2.js";
import {
  ButtonView,
  CollapsibleView,
  ContextualBalloon,
  CssTransitionDisablerMixin,
  FocusCycler,
  LabeledFieldView,
  SplitButtonView,
  View,
  ViewCollection,
  balloonpanelview_default,
  clickOutsideHandler,
  createDropdown,
  createLabeledInputText,
  submitHandler
} from "./chunk-CCHBLTQG.js";
import "./chunk-J3DZJUY2.js";
import {
  Command,
  FocusTracker,
  KeystrokeHandler,
  Plugin,
  UpcastWriter,
  icons,
  logWarning
} from "./chunk-XX3CIG5U.js";
import "./chunk-624QZG55.js";

// node_modules/@ckeditor/ckeditor5-image/src/imagetextalternative/imagetextalternativecommand.js
var ImageTextAlternativeCommand = class extends Command {
  /**
   * @inheritDoc
   */
  refresh() {
    const editor = this.editor;
    const imageUtils = editor.plugins.get("ImageUtils");
    const element = imageUtils.getClosestSelectedImageElement(this.editor.model.document.selection);
    this.isEnabled = !!element;
    if (this.isEnabled && element.hasAttribute("alt")) {
      this.value = element.getAttribute("alt");
    } else {
      this.value = false;
    }
  }
  /**
   * Executes the command.
   *
   * @fires execute
   * @param options
   * @param options.newValue The new value of the `alt` attribute to set.
   */
  execute(options) {
    const editor = this.editor;
    const imageUtils = editor.plugins.get("ImageUtils");
    const model = editor.model;
    const imageElement = imageUtils.getClosestSelectedImageElement(model.document.selection);
    model.change((writer) => {
      writer.setAttribute("alt", options.newValue, imageElement);
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagetextalternative/imagetextalternativeediting.js
var ImageTextAlternativeEditing = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageUtils];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageTextAlternativeEditing";
  }
  /**
   * @inheritDoc
   */
  init() {
    this.editor.commands.add("imageTextAlternative", new ImageTextAlternativeCommand(this.editor));
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagetextalternative/ui/textalternativeformview.js
import "/tmp/build_6afe4207/node_modules/@ckeditor/ckeditor5-image/theme/textalternativeform.css";
import "/tmp/build_6afe4207/node_modules/@ckeditor/ckeditor5-ui/theme/components/responsive-form/responsiveform.css";
var TextAlternativeFormView = class extends View {
  /**
   * @inheritDoc
   */
  constructor(locale) {
    super(locale);
    const t = this.locale.t;
    this.focusTracker = new FocusTracker();
    this.keystrokes = new KeystrokeHandler();
    this.labeledInput = this._createLabeledInputView();
    this.saveButtonView = this._createButton(t("Save"), icons.check, "ck-button-save");
    this.saveButtonView.type = "submit";
    this.cancelButtonView = this._createButton(t("Cancel"), icons.cancel, "ck-button-cancel", "cancel");
    this._focusables = new ViewCollection();
    this._focusCycler = new FocusCycler({
      focusables: this._focusables,
      focusTracker: this.focusTracker,
      keystrokeHandler: this.keystrokes,
      actions: {
        // Navigate form fields backwards using the Shift + Tab keystroke.
        focusPrevious: "shift + tab",
        // Navigate form fields forwards using the Tab key.
        focusNext: "tab"
      }
    });
    this.setTemplate({
      tag: "form",
      attributes: {
        class: [
          "ck",
          "ck-text-alternative-form",
          "ck-responsive-form"
        ],
        // https://github.com/ckeditor/ckeditor5-image/issues/40
        tabindex: "-1"
      },
      children: [
        this.labeledInput,
        this.saveButtonView,
        this.cancelButtonView
      ]
    });
  }
  /**
   * @inheritDoc
   */
  render() {
    super.render();
    this.keystrokes.listenTo(this.element);
    submitHandler({ view: this });
    [this.labeledInput, this.saveButtonView, this.cancelButtonView].forEach((v) => {
      this._focusables.add(v);
      this.focusTracker.add(v.element);
    });
  }
  /**
   * @inheritDoc
   */
  destroy() {
    super.destroy();
    this.focusTracker.destroy();
    this.keystrokes.destroy();
  }
  /**
   * Creates the button view.
   *
   * @param label The button label
   * @param icon The button's icon.
   * @param className The additional button CSS class name.
   * @param eventName The event name that the ButtonView#execute event will be delegated to.
   * @returns The button view instance.
   */
  _createButton(label, icon, className, eventName) {
    const button = new ButtonView(this.locale);
    button.set({
      label,
      icon,
      tooltip: true
    });
    button.extendTemplate({
      attributes: {
        class: className
      }
    });
    if (eventName) {
      button.delegate("execute").to(this, eventName);
    }
    return button;
  }
  /**
   * Creates an input with a label.
   *
   * @returns Labeled field view instance.
   */
  _createLabeledInputView() {
    const t = this.locale.t;
    const labeledInput = new LabeledFieldView(this.locale, createLabeledInputText);
    labeledInput.label = t("Text alternative");
    return labeledInput;
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/image/ui/utils.js
function repositionContextualBalloon(editor) {
  const balloon = editor.plugins.get("ContextualBalloon");
  const imageUtils = editor.plugins.get("ImageUtils");
  if (imageUtils.getClosestSelectedImageWidget(editor.editing.view.document.selection)) {
    const position = getBalloonPositionData(editor);
    balloon.updatePosition(position);
  }
}
function getBalloonPositionData(editor) {
  const editingView = editor.editing.view;
  const defaultPositions = balloonpanelview_default.defaultPositions;
  const imageUtils = editor.plugins.get("ImageUtils");
  return {
    target: editingView.domConverter.mapViewToDom(imageUtils.getClosestSelectedImageWidget(editingView.document.selection)),
    positions: [
      defaultPositions.northArrowSouth,
      defaultPositions.northArrowSouthWest,
      defaultPositions.northArrowSouthEast,
      defaultPositions.southArrowNorth,
      defaultPositions.southArrowNorthWest,
      defaultPositions.southArrowNorthEast,
      defaultPositions.viewportStickyNorth
    ]
  };
}

// node_modules/@ckeditor/ckeditor5-image/src/imagetextalternative/imagetextalternativeui.js
var ImageTextAlternativeUI = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ContextualBalloon];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageTextAlternativeUI";
  }
  /**
   * @inheritDoc
   */
  init() {
    this._createButton();
  }
  /**
   * @inheritDoc
   */
  destroy() {
    super.destroy();
    if (this._form) {
      this._form.destroy();
    }
  }
  /**
   * Creates a button showing the balloon panel for changing the image text alternative and
   * registers it in the editor {@link module:ui/componentfactory~ComponentFactory ComponentFactory}.
   */
  _createButton() {
    const editor = this.editor;
    const t = editor.t;
    editor.ui.componentFactory.add("imageTextAlternative", (locale) => {
      const command = editor.commands.get("imageTextAlternative");
      const view = new ButtonView(locale);
      view.set({
        label: t("Change image text alternative"),
        icon: icons.textAlternative,
        tooltip: true
      });
      view.bind("isEnabled").to(command, "isEnabled");
      view.bind("isOn").to(command, "value", (value) => !!value);
      this.listenTo(view, "execute", () => {
        this._showForm();
      });
      return view;
    });
  }
  /**
   * Creates the {@link module:image/imagetextalternative/ui/textalternativeformview~TextAlternativeFormView}
   * form.
   */
  _createForm() {
    const editor = this.editor;
    const view = editor.editing.view;
    const viewDocument = view.document;
    const imageUtils = editor.plugins.get("ImageUtils");
    this._balloon = this.editor.plugins.get("ContextualBalloon");
    this._form = new (CssTransitionDisablerMixin(TextAlternativeFormView))(editor.locale);
    this._form.render();
    this.listenTo(this._form, "submit", () => {
      editor.execute("imageTextAlternative", {
        newValue: this._form.labeledInput.fieldView.element.value
      });
      this._hideForm(true);
    });
    this.listenTo(this._form, "cancel", () => {
      this._hideForm(true);
    });
    this._form.keystrokes.set("Esc", (data, cancel) => {
      this._hideForm(true);
      cancel();
    });
    this.listenTo(editor.ui, "update", () => {
      if (!imageUtils.getClosestSelectedImageWidget(viewDocument.selection)) {
        this._hideForm(true);
      } else if (this._isVisible) {
        repositionContextualBalloon(editor);
      }
    });
    clickOutsideHandler({
      emitter: this._form,
      activator: () => this._isVisible,
      contextElements: () => [this._balloon.view.element],
      callback: () => this._hideForm()
    });
  }
  /**
   * Shows the {@link #_form} in the {@link #_balloon}.
   */
  _showForm() {
    if (this._isVisible) {
      return;
    }
    if (!this._form) {
      this._createForm();
    }
    const editor = this.editor;
    const command = editor.commands.get("imageTextAlternative");
    const labeledInput = this._form.labeledInput;
    this._form.disableCssTransitions();
    if (!this._isInBalloon) {
      this._balloon.add({
        view: this._form,
        position: getBalloonPositionData(editor)
      });
    }
    labeledInput.fieldView.value = labeledInput.fieldView.element.value = command.value || "";
    this._form.labeledInput.fieldView.select();
    this._form.enableCssTransitions();
  }
  /**
   * Removes the {@link #_form} from the {@link #_balloon}.
   *
   * @param focusEditable Controls whether the editing view is focused afterwards.
   */
  _hideForm(focusEditable = false) {
    if (!this._isInBalloon) {
      return;
    }
    if (this._form.focusTracker.isFocused) {
      this._form.saveButtonView.focus();
    }
    this._balloon.remove(this._form);
    if (focusEditable) {
      this.editor.editing.view.focus();
    }
  }
  /**
   * Returns `true` when the {@link #_form} is the visible view in the {@link #_balloon}.
   */
  get _isVisible() {
    return !!this._balloon && this._balloon.visibleView === this._form;
  }
  /**
   * Returns `true` when the {@link #_form} is in the {@link #_balloon}.
   */
  get _isInBalloon() {
    return !!this._balloon && this._balloon.hasView(this._form);
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagetextalternative.js
var ImageTextAlternative = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageTextAlternativeEditing, ImageTextAlternativeUI];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageTextAlternative";
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imageinsert/ui/imageinsertformview.js
import "/tmp/build_6afe4207/node_modules/@ckeditor/ckeditor5-image/theme/imageinsert.css";
var ImageInsertFormView = class extends View {
  /**
   * Creates a view for the dropdown panel of {@link module:image/imageinsert/imageinsertui~ImageInsertUI}.
   *
   * @param locale The localization services instance.
   * @param integrations An integrations object that contains components (or tokens for components) to be shown in the panel view.
   */
  constructor(locale, integrations = []) {
    super(locale);
    this.focusTracker = new FocusTracker();
    this.keystrokes = new KeystrokeHandler();
    this._focusables = new ViewCollection();
    this.children = this.createCollection();
    this._focusCycler = new FocusCycler({
      focusables: this._focusables,
      focusTracker: this.focusTracker,
      keystrokeHandler: this.keystrokes,
      actions: {
        // Navigate form fields backwards using the Shift + Tab keystroke.
        focusPrevious: "shift + tab",
        // Navigate form fields forwards using the Tab key.
        focusNext: "tab"
      }
    });
    for (const view of integrations) {
      this.children.add(view);
      this._focusables.add(view);
      if (view instanceof CollapsibleView) {
        this._focusables.addMany(view.children);
      }
    }
    if (this._focusables.length > 1) {
      for (const view of this._focusables) {
        if (isViewWithFocusCycler(view)) {
          view.focusCycler.on("forwardCycle", (evt) => {
            this._focusCycler.focusNext();
            evt.stop();
          });
          view.focusCycler.on("backwardCycle", (evt) => {
            this._focusCycler.focusPrevious();
            evt.stop();
          });
        }
      }
    }
    this.setTemplate({
      tag: "form",
      attributes: {
        class: [
          "ck",
          "ck-image-insert-form"
        ],
        tabindex: -1
      },
      children: this.children
    });
  }
  /**
   * @inheritDoc
   */
  render() {
    super.render();
    submitHandler({
      view: this
    });
    for (const view of this._focusables) {
      this.focusTracker.add(view.element);
    }
    this.keystrokes.listenTo(this.element);
    const stopPropagation = (data) => data.stopPropagation();
    this.keystrokes.set("arrowright", stopPropagation);
    this.keystrokes.set("arrowleft", stopPropagation);
    this.keystrokes.set("arrowup", stopPropagation);
    this.keystrokes.set("arrowdown", stopPropagation);
  }
  /**
   * @inheritDoc
   */
  destroy() {
    super.destroy();
    this.focusTracker.destroy();
    this.keystrokes.destroy();
  }
  /**
   * Focuses the first {@link #_focusables focusable} in the form.
   */
  focus() {
    this._focusCycler.focusFirst();
  }
};
function isViewWithFocusCycler(view) {
  return "focusCycler" in view;
}

// node_modules/@ckeditor/ckeditor5-image/src/imageinsert/imageinsertui.js
var ImageInsertUI = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageInsertUI";
  }
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageUtils];
  }
  /**
   * @inheritDoc
   */
  constructor(editor) {
    super(editor);
    this._integrations = /* @__PURE__ */ new Map();
    editor.config.define("image.insert.integrations", [
      "upload",
      "assetManager",
      "url"
    ]);
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const selection = editor.model.document.selection;
    const imageUtils = editor.plugins.get("ImageUtils");
    this.set("isImageSelected", false);
    this.listenTo(editor.model.document, "change", () => {
      this.isImageSelected = imageUtils.isImage(selection.getSelectedElement());
    });
    const componentCreator = (locale) => this._createToolbarComponent(locale);
    editor.ui.componentFactory.add("insertImage", componentCreator);
    editor.ui.componentFactory.add("imageInsert", componentCreator);
  }
  /**
   * Registers the insert image dropdown integration.
   */
  registerIntegration({ name, observable, buttonViewCreator, formViewCreator, requiresForm }) {
    if (this._integrations.has(name)) {
      logWarning("image-insert-integration-exists", { name });
    }
    this._integrations.set(name, {
      observable,
      buttonViewCreator,
      formViewCreator,
      requiresForm: !!requiresForm
    });
  }
  /**
   * Creates the toolbar component.
   */
  _createToolbarComponent(locale) {
    const editor = this.editor;
    const t = locale.t;
    const integrations = this._prepareIntegrations();
    if (!integrations.length) {
      return null;
    }
    let dropdownButton;
    const firstIntegration = integrations[0];
    if (integrations.length == 1) {
      if (!firstIntegration.requiresForm) {
        return firstIntegration.buttonViewCreator(true);
      }
      dropdownButton = firstIntegration.buttonViewCreator(true);
    } else {
      const actionButton = firstIntegration.buttonViewCreator(false);
      dropdownButton = new SplitButtonView(locale, actionButton);
      dropdownButton.tooltip = true;
      dropdownButton.bind("label").to(this, "isImageSelected", (isImageSelected) => isImageSelected ? t("Replace image") : t("Insert image"));
    }
    const dropdownView = this.dropdownView = createDropdown(locale, dropdownButton);
    const observables = integrations.map(({ observable }) => observable);
    dropdownView.bind("isEnabled").toMany(observables, "isEnabled", (...isEnabled) => isEnabled.some((isEnabled2) => isEnabled2));
    dropdownView.once("change:isOpen", () => {
      const integrationViews = integrations.map(({ formViewCreator }) => formViewCreator(integrations.length == 1));
      const imageInsertFormView = new ImageInsertFormView(editor.locale, integrationViews);
      dropdownView.panelView.children.add(imageInsertFormView);
    });
    return dropdownView;
  }
  /**
   * Validates the integrations list.
   */
  _prepareIntegrations() {
    const editor = this.editor;
    const items = editor.config.get("image.insert.integrations");
    const result = [];
    if (!items.length) {
      logWarning("image-insert-integrations-not-specified");
      return result;
    }
    for (const item of items) {
      if (!this._integrations.has(item)) {
        if (!["upload", "assetManager", "url"].includes(item)) {
          logWarning("image-insert-unknown-integration", { item });
        }
        continue;
      }
      result.push(this._integrations.get(item));
    }
    if (!result.length) {
      logWarning("image-insert-integrations-not-registered");
    }
    return result;
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imageblock.js
import "/tmp/build_6afe4207/node_modules/@ckeditor/ckeditor5-image/theme/image.css";
var ImageBlock = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageBlockEditing, Widget, ImageTextAlternative, ImageInsertUI];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageBlock";
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/image/imageinlineediting.js
var ImageInlineEditing = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageEditing, ImageSizeAttributes, ImageUtils, ImagePlaceholder, ClipboardPipeline];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageInlineEditing";
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const schema = editor.model.schema;
    schema.register("imageInline", {
      inheritAllFrom: "$inlineObject",
      allowAttributes: ["alt", "src", "srcset"]
    });
    schema.addChildCheck((context, childDefinition) => {
      if (context.endsWith("caption") && childDefinition.name === "imageInline") {
        return false;
      }
    });
    this._setupConversion();
    if (editor.plugins.has("ImageBlockEditing")) {
      editor.commands.add("imageTypeInline", new ImageTypeCommand(this.editor, "imageInline"));
      this._setupClipboardIntegration();
    }
  }
  /**
   * Configures conversion pipelines to support upcasting and downcasting
   * inline images (inline image widgets) and their attributes.
   */
  _setupConversion() {
    const editor = this.editor;
    const t = editor.t;
    const conversion = editor.conversion;
    const imageUtils = editor.plugins.get("ImageUtils");
    conversion.for("dataDowncast").elementToElement({
      model: "imageInline",
      view: (modelElement, { writer }) => writer.createEmptyElement("img")
    });
    conversion.for("editingDowncast").elementToStructure({
      model: "imageInline",
      view: (modelElement, { writer }) => imageUtils.toImageWidget(createInlineImageViewElement(writer), writer, t("image widget"))
    });
    conversion.for("downcast").add(downcastImageAttribute(imageUtils, "imageInline", "src")).add(downcastImageAttribute(imageUtils, "imageInline", "alt")).add(downcastSrcsetAttribute(imageUtils, "imageInline"));
    conversion.for("upcast").elementToElement({
      view: getImgViewElementMatcher(editor, "imageInline"),
      model: (viewImage, { writer }) => writer.createElement("imageInline", viewImage.hasAttribute("src") ? { src: viewImage.getAttribute("src") } : void 0)
    });
  }
  /**
   * Integrates the plugin with the clipboard pipeline.
   *
   * Idea is that the feature should recognize the user's intent when an **block** image is
   * pasted or dropped. If such an image is pasted/dropped into a non-empty block
   * (e.g. a paragraph with some text) it gets converted into an inline image on the fly.
   *
   * We assume this is the user's intent if they decided to put their image there.
   *
   * **Note**: If a block image has a caption, it will not be converted to an inline image
   * to avoid the confusion. Captions are added on purpose and they should never be lost
   * in the clipboard pipeline.
   *
   * See the `ImageBlockEditing` for the similar integration that works in the opposite direction.
   *
   * The feature also sets image `width` and `height` attributes when pasting.
   */
  _setupClipboardIntegration() {
    const editor = this.editor;
    const model = editor.model;
    const editingView = editor.editing.view;
    const imageUtils = editor.plugins.get("ImageUtils");
    const clipboardPipeline = editor.plugins.get("ClipboardPipeline");
    this.listenTo(clipboardPipeline, "inputTransformation", (evt, data) => {
      const docFragmentChildren = Array.from(data.content.getChildren());
      let modelRange;
      if (!docFragmentChildren.every(imageUtils.isBlockImageView)) {
        return;
      }
      if (data.targetRanges) {
        modelRange = editor.editing.mapper.toModelRange(data.targetRanges[0]);
      } else {
        modelRange = model.document.selection.getFirstRange();
      }
      const selection = model.createSelection(modelRange);
      if (determineImageTypeForInsertionAtSelection(model.schema, selection) === "imageInline") {
        const writer = new UpcastWriter(editingView.document);
        const inlineViewImages = docFragmentChildren.map((blockViewImage) => {
          if (blockViewImage.childCount === 1) {
            Array.from(blockViewImage.getAttributes()).forEach((attribute) => writer.setAttribute(...attribute, imageUtils.findViewImgElement(blockViewImage)));
            return blockViewImage.getChild(0);
          } else {
            return blockViewImage;
          }
        });
        data.content = writer.createDocumentFragment(inlineViewImages);
      }
    });
    this.listenTo(clipboardPipeline, "contentInsertion", (evt, data) => {
      if (data.method !== "paste") {
        return;
      }
      model.change((writer) => {
        const range = writer.createRangeIn(data.content);
        for (const item of range.getItems()) {
          if (item.is("element", "imageInline")) {
            imageUtils.setImageNaturalSizeAttributes(item);
          }
        }
      });
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imageinline.js
import "/tmp/build_6afe4207/node_modules/@ckeditor/ckeditor5-image/theme/image.css";
var ImageInline = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageInlineEditing, Widget, ImageTextAlternative, ImageInsertUI];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageInline";
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/image.js
import "/tmp/build_6afe4207/node_modules/@ckeditor/ckeditor5-image/theme/image.css";
var Image = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageBlock, ImageInline];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "Image";
  }
};
export {
  Image as default
};
/*! Bundled license information:

@ckeditor/ckeditor5-image/src/imagetextalternative/imagetextalternativecommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagetextalternative/imagetextalternativeediting.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagetextalternative/ui/textalternativeformview.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/image/ui/utils.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagetextalternative/imagetextalternativeui.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagetextalternative.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imageinsert/ui/imageinsertformview.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imageinsert/imageinsertui.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imageblock.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/image/imageinlineediting.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imageinline.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/image.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
*/
//# sourceMappingURL=@ckeditor_ckeditor5-image_src_image.js.map
